/*
 * Given a clock value string and period return game time in minutes as string
 */
const clockAndPeriodToMinute = (clock, period, fixtureProfile) => {
  const minuteFromClock = clock.substring(2).split("M")[0];
  return (
    fixtureProfile.periodLength -
    Number(minuteFromClock) +
    (period - 1) * fixtureProfile.periodLength
  );
};

/*
 * Given a goal event return type of goal. Possible values: PS, PC and FG
 */
export const goalEventToAction = (goalEvent) => {
  if (goalEvent.eventType === "penaltyStroke") return "PS";
  if (goalEvent?.options?.goalOutcome === "PENALTY_CORNER") return "PC";
  return "FG";
};

export const scoresToScoreString = (homeEntityId, awayEntityId) => (scores) =>
  `${scores[homeEntityId]} - ${scores[awayEntityId]}`;

/*
 * Given the PBP array returns an array of scoring action objects
 */
export const calculateScoringFromPbp = (
  pbp,
  scoreStrBuilder,
  playerLookup,
  teams,
  fixtureProfile
) => {
  const goalsMade = pbp.filter(
    (e) =>
      (e.eventType === "goal" || e.eventType === "penaltyStroke") && e.success
  );
  const goals = goalsMade.map((goalEvent) => {
    const { clock, entityId, periodId, personId, scores } = goalEvent;
    return {
      action: goalEventToAction(goalEvent),
      minute: clockAndPeriodToMinute(clock, periodId, fixtureProfile),
      number: playerLookup[entityId]?.[personId]?.bib ?? "",
      score: scoreStrBuilder(scores),
      team:
        teams[entityId].code ??
        teams[entityId].name.substring(0, 3).toUpperCase(),
    };
  });
  return goals;
};

/*
 * Given the PBP array return an object that maps a player's personId to the time of their first substitution
 */
export const calculateSubsFromPbp = (pbp, fixtureProfile) => {
  const subsIn = pbp.filter(
    (e) => e.eventType === "substitution" && e.subType === "in"
  );
  const subs = subsIn.reduce((acc, sub) => {
    // this person has been already subbed in
    if (acc[sub.entityId]?.[sub.personId]) return acc;
    if (!acc[sub.entityId])
      return {
        ...acc,
        [sub.entityId]: {
          [sub.personId]: clockAndPeriodToMinute(
            sub.clock,
            sub.periodId,
            fixtureProfile
          ),
        },
      };
    return {
      ...acc,
      [sub.entityId]: {
        ...acc[sub.entityId],
        [sub.personId]: clockAndPeriodToMinute(
          sub.clock,
          sub.periodId,
          fixtureProfile
        ),
      },
    };
  }, {});
  return subs;
};

/*
 * Given the PBP array return an object that maps an array of all card events to a player personId
 */
export const calculateCardsFromPbp = (pbp, fixtureProfile) => {
  const allCards = pbp.filter(
    // subType start/stop means countDown event, these need to be filtered out
    (ev) =>
      ["greenCard", "yellowCard", "redCard"].includes(ev.eventType) &&
      ev.subType === null
  );
  const cards = allCards.reduce((acc, card) => {
    const result = { ...acc };
    if (!result[card.entityId]) result[card.entityId] = {};
    if (!result[card.entityId][card.personId])
      result[card.entityId][card.personId] = [];
    result[card.entityId][card.personId].push({
      minute: clockAndPeriodToMinute(card.clock, card.periodId, fixtureProfile),
      personId: card.personId,
      type: card.eventType,
      reason: card?.options?.cardReason ?? null,
      duration: card?.options?.time ?? null,
    });
    return result;
  }, {});
  return cards;
};

/*
 * Given an array of player objects build a lookup table with player's personId as key
 */
export const buildPlayerLookup = (players) =>
  players.reduce((acc, player) => {
    return { ...acc, [player.personId]: player };
  }, {});

/*
 * Given a periodId and an array of scoring intervals return last interval for that period.
 * Order is deduced from interval_number property of scoring intervals
 */
const filterForLastInterval = (periodId, intervals) => {
  const filtered = intervals.filter(
    (interval) => interval.periodId === periodId
  );
  const sorted = filtered.sort((a, b) => a.interval_number - b.interval_number);
  return sorted.at(-1);
};

/*
 * Given scoring intervals array from server create a list of labels to display as scoring intervals on report
 */
export const processScoringIntervals = (
  scoringIntervals,
  homeEntityId,
  awayEntityId
) => {
  const safePrintIntervalLabel = (interval) => {
    const homeScore = interval?.[homeEntityId] ?? "";
    const awayScore = interval?.[awayEntityId] ?? "";
    return `${homeScore} - ${awayScore}`;
  };
  const firstPeriod = filterForLastInterval(1, scoringIntervals);
  const halfTime = filterForLastInterval(2, scoringIntervals);
  const thirdPeriod = filterForLastInterval(3, scoringIntervals);
  const fullTime = filterForLastInterval(4, scoringIntervals);
  return [
    safePrintIntervalLabel(firstPeriod),
    safePrintIntervalLabel(halfTime),
    safePrintIntervalLabel(thirdPeriod),
    safePrintIntervalLabel(fullTime),
  ];
};

export const countShootoutGoalsByTeam = (pbp, teamId) => {
  return pbp.filter((e) => ["shootOutAttempt"].includes(e.eventType)
    && ["made"].includes(e.subType)
    && e.entityId === teamId).length;
};
